import { ContentHealthStatus, QuartrHealthStatus } from '../../types/contentCompletion';

/** Display labels — API values stay active/stale/no_content/no_feed. */
export const PR_STATUS_LABELS: Record<ContentHealthStatus, string> = {
  active: 'Recent',
  stale: 'Quiet',
  no_content: 'No PRs yet',
  no_feed: 'Needs feed',
};

export const QUARTR_STATUS_LABELS: Record<QuartrHealthStatus, string> = {
  active: 'Recent',
  stale: 'Quiet',
  no_content: 'No events yet',
  no_feed: 'Needs Quartr',
  missing_id: 'Missing ID',
};

export const prStatusTooltip = (
  status: ContentHealthStatus,
  staleDays: number,
  lastPrDate?: string | null
): string => {
  const daysAgo = daysSince(lastPrDate);
  switch (status) {
    case 'active':
      return `Feed configured; press release in the last ${staleDays} days.`;
    case 'stale':
      return daysAgo != null
        ? `Feed configured; last press release was ${daysAgo} day${daysAgo === 1 ? '' : 's'} ago (outside the ${staleDays}-day recent window). Company may still be active — just quiet.`
        : `Feed configured with older press releases, but none in the last ${staleDays} days. Company may still be active — just quiet.`;
    case 'no_content':
      return 'Feed URL is configured, but no press releases have been ingested yet.';
    case 'no_feed':
      return 'No press-release or RSS URL configured — we cannot ingest PRs until a feed is set.';
    default:
      return '';
  }
};

export const quartrStatusTooltip = (
  status: QuartrHealthStatus,
  staleDays: number,
  lastQuartrDate?: string | null
): string => {
  const daysAgo = daysSince(lastQuartrDate);
  switch (status) {
    case 'active':
      return `Quartr linked; event/content in the last ${staleDays} days.`;
    case 'stale':
      return daysAgo != null
        ? `Quartr linked; last event was ${daysAgo} day${daysAgo === 1 ? '' : 's'} ago (outside the ${staleDays}-day recent window).`
        : `Quartr linked with older events, but none in the last ${staleDays} days.`;
    case 'no_content':
      return 'Quartr company ID is set, but no Quartr events have been ingested yet.';
    case 'no_feed':
      return 'No Quartr company ID configured.';
    case 'missing_id':
      return 'Public company with a ticker but no Quartr company ID.';
    default:
      return '';
  }
};

export const daysSince = (value?: string | null): number | null => {
  if (!value) return null;
  const then = new Date(value);
  if (Number.isNaN(then.getTime())) return null;
  const ms = Date.now() - then.getTime();
  if (ms < 0) return 0;
  return Math.floor(ms / (1000 * 60 * 60 * 24));
};

export const formatDate = (value?: string | null): string => {
  if (!value) return '—';
  // Published dates are calendar days stored as UTC datetimes (often midnight
  // or noon). Format in UTC so US local timezones don't show the previous day.
  const dateOnly = value.match(/^(\d{4}-\d{2}-\d{2})/);
  if (dateOnly) {
    const [y, m, d] = dateOnly[1].split('-').map(Number);
    return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString(undefined, {
      timeZone: 'UTC',
    });
  }
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(undefined, { timeZone: 'UTC' });
};

/** e.g. "12 May 2026 (59 days ago)" or "—" */
export const formatLastContentDate = (value?: string | null): string => {
  if (!value) return '—';
  const label = formatDate(value);
  const days = daysSince(value);
  if (days == null) return label;
  if (days === 0) return `${label} (today)`;
  if (days === 1) return `${label} (1 day ago)`;
  return `${label} (${days} days ago)`;
};
